<template>
  <q-page class="com">

    <!-- Глобальный индикатор загрузки при смене viewMode -->
    <Transition name="fade">
      <div v-if="isChangingViewMode" class="global-loading-overlay">
        <div class="global-loading-content">
          <q-spinner-dots color="primary" size="50px"/>
          <div class="global-loading-text">Переключение режима...</div>
        </div>
      </div>
    </Transition>

    <!-- Stories -->
    <div class="q-mx-md q-mx-md-none q-pt-md" v-if="false">
      <StoriesList
          :stories="stories"
          effect="cube"
          @viewed="handleStoryViewed"
      />
    </div>

    <div
        class="q-mx-md q-mx-lg-none"
        id="sections"
    >
      <Sections class="q-pb-xl"/>
    </div>

    <!-- Product Collections -->
    <div
        class="q-pt-md q-pb-lg"
        id="collections"
        v-intersection="scrollNav.intersectDirectiveConnect('collections')"
        v-if="false"
    >
      <ProductCollections @filtered="onCollectionFiltered"/>
    </div>

    <PopularProductsCarousel v-if="false"/>

    <div
        class="q-gutter-xl"
        ref="scrollContainer"
        v-intersection="scrollContainerIntersectHandler"
    >

      <!-- Рендерим только загруженные секции -->
      <!-- LazyHydrationWrapper НЕ используется, так как секции добавляются динамически -->
      <div
          v-for="(section, index) in sectionsToRender"
          :key="section.ID"
          v-intersection="createIntersectHandler(section.CODE || '', index)"
          :id="section.CODE || ''"
      >
        <ProductSectionView :section="section" @filtered="onSectionFiltered"/>
      </div>

      <!-- Индикатор загрузки для незагруженных секций (опционально) -->
      <div
          v-if="sectionVisitedIndex < sectionsTree.length && DEBUG_LAZY_LOAD"
          class="lazy-load-indicator q-pa-md text-center text-grey-6"
      >
        Загружено {{ sectionVisitedIndex }} из {{ sectionsTree.length }} секций
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import PopularProductsCarousel from "@/components/Shop/PopularProducts/PopularProductsCarousel.vue";
import ProductCollections from "@/components/Shop/ProductCollection/ProductCollections.vue";
import ProductSectionView from "@/components/Shop/ProductSection/Section.vue";
import Sections from "@/components/Shop/ProductSection/Sections.vue";
import {StoriesList, type Story} from "@/components/stories";
import {useBus} from "@/core/store/bus";
import {getElementTopOffset} from "@/core/util/dom";
import {ssrSafeNumberDebounce} from "@/core/util/ssrSafeNumberDebounce";
import {ProductSection} from "@/gql/gen";
import {usePage} from "@/modules/page/store/pages";
import {useCategorySwipe} from "@/modules/shop/composable/useCategorySwipe";
import {useCatalogStore} from "@/modules/shop/store/catalog";
import {useLayoutStore} from "@/stores/layout";
import {usePageFrontStore} from "@/stores/page-front";
import {storeToRefs} from "pinia";
import {IntersectionValue, useMeta, useQuasar} from "quasar";
import {computed, onMounted, onUnmounted, ref, toValue, watch} from "vue";

// Определяем имя компонента для keep-alive
defineOptions({
  name: 'FrontPage'
});

const $q = useQuasar();

// Константы для lazy load секций
const INITIAL_SECTIONS_COUNT = 1; // Количество секций для начальной загрузки
const PRELOAD_SECTIONS_COUNT = 2; // Количество секций для предзагрузки при скролле
const SECTION_INDEX_UPDATE_DEBOUNCE = 70; // Задержка для debounce обновлений индекса (ms)
const DEBUG_LAZY_LOAD = false; // Флаг отладки

const catalogStore = useCatalogStore();
const {sectionsTree, isChangingViewMode} = storeToRefs(catalogStore);

const {bus} = useBus();

const pageFrontStore = usePageFrontStore();

const {scrollNav, setActiveSectionSilent} = pageFrontStore;

const scrollContainer = ref<HTMLElement>();

const layoutStore = useLayoutStore();
const {headerHeight} = storeToRefs(layoutStore);

// Флаг для отслеживания монтирования на клиенте (для SSR-совместимости)
const isClientMounted = ref(false);

// Флаг для отслеживания начальной навигации (защита от ложных срабатываний)
// Используем обычную переменную вместо ref чтобы избежать ререндера
// Всегда true при инициализации, сбрасывается после первого scrollend
let isInitialNavigation = true;

// Таймер для сброса флага начальной навигации
let initialNavigationTimeout: NodeJS.Timeout | null = null;

// Функция для получения начального количества секций на основе hash
// ВАЖНО: Вызывается синхронно при инициализации для предотвращения hydration mismatch
const getInitialSectionCount = (): number => {
  // На сервере ВСЕГДА загружаем INITIAL_SECTIONS_COUNT
  // ВАЖНО: Hash (#section) не передается на сервер в HTTP запросе!
  // Hash доступен только на клиенте через window.location.hash
  if (process.env.SERVER) {
    if (DEBUG_LAZY_LOAD) {
      console.log('[LazyLoad SSR] Server init (hash not available on server):', INITIAL_SECTIONS_COUNT);
    }
    return INITIAL_SECTIONS_COUNT;
  }

  // На клиенте проверяем window.location.hash
  if (typeof window === 'undefined') {
    return INITIAL_SECTIONS_COUNT;
  }

  const currentHash = window.location.hash.replace('#', '');

  if (!currentHash) {
    return INITIAL_SECTIONS_COUNT;
  }

  // Находим индекс секции по hash
  const sectionIndex = sectionsTree.value.findIndex(
      (s: ProductSection) => s.CODE === currentHash
  );

  if (sectionIndex !== -1) {
    if (DEBUG_LAZY_LOAD) {
      console.log('[LazyLoad] Client init with hash:', {
        hash: currentHash,
        index: sectionIndex,
        loading: sectionIndex + 1 + PRELOAD_SECTIONS_COUNT
      });
    }

    // Загружаем секции до нужной + предзагрузка
    return Math.min(
        sectionIndex + 1 + PRELOAD_SECTIONS_COUNT,
        sectionsTree.value.length
    );
  }

  return INITIAL_SECTIONS_COUNT;
};

// Lazy load state
// sectionVisitedIndex определяет количество секций для рендеринга
// Значение может только увеличиваться, никогда не уменьшается
// ВАЖНО: Инициализируется с учетом hash ДО гидратации для предотвращения hydration mismatch
const sectionVisitedIndex = ref(getInitialSectionCount());

// Debounced функция для обновления индекса секций
// Накапливает МАКСИМАЛЬНОЕ значение за период ожидания
// Используется для событий (intersection, navigation), но НЕ для инициализации
const updateSectionIndexDebounced = ssrSafeNumberDebounce((newIndex: number) => {
  if (newIndex > sectionVisitedIndex.value) {
    if (DEBUG_LAZY_LOAD) {
      console.log('[LazyLoad] Debounced update:', {
        from: sectionVisitedIndex.value,
        to: newIndex,
        total: sectionsTree.value.length
      });
    }
    sectionVisitedIndex.value = newIndex;
  }
}, SECTION_INDEX_UPDATE_DEBOUNCE, 'max');

// Вычисляемое свойство для секций которые нужно отрендерить
const sectionsToRender = computed(() => {
  return sectionsTree.value.slice(0, sectionVisitedIndex.value);
});

// Функция для увеличения количества загруженных секций
// Используется при IntersectionObserver событиях (скролл)
const loadMoreSections = (count: number = PRELOAD_SECTIONS_COUNT) => {
  const newIndex = Math.min(
      sectionVisitedIndex.value + count,
      sectionsTree.value.length
  );

  if (newIndex > sectionVisitedIndex.value) {
    // Используем debounced обновление для событий скролла
    updateSectionIndexDebounced(newIndex);
  }
};

// Функция для загрузки секции по коду (для навигации по меню)
// Используется при клике в меню или программной навигации
const ensureSectionLoaded = (sectionCode: string) => {
  // Защита от вызова до монтирования на клиенте
  if (!isClientMounted.value && !process.env.SERVER) {
    if (DEBUG_LAZY_LOAD) {
      console.log('[LazyLoad] Skipping ensureSectionLoaded - not mounted yet:', sectionCode);
    }
    return;
  }

  const sectionIndex = sectionsTree.value.findIndex(
      (s: ProductSection) => s.CODE === sectionCode
  );

  if (sectionIndex !== -1 && sectionIndex >= sectionVisitedIndex.value) {
    const newIndex = Math.min(
        sectionIndex + 1 + PRELOAD_SECTIONS_COUNT,
        sectionsTree.value.length
    );

    if (DEBUG_LAZY_LOAD) {
      console.log('[LazyLoad] Ensuring section loaded:', {
        code: sectionCode,
        index: sectionIndex,
        currentVisited: sectionVisitedIndex.value,
        newIndex
      });
    }

    // Для навигации используем немедленное обновление (flush debounce)
    // чтобы секция была доступна сразу для скролла
    updateSectionIndexDebounced.cancel(); // Отменяем накопленные обновления
    sectionVisitedIndex.value = newIndex; // Обновляем напрямую
  }
};

// Мертвая зона для предотвращения лишних скроллов (в пикселях)
// Если элемент находится в пределах этой зоны от header - не скроллим
const SCROLL_DEAD_ZONE = 100;

// Обертка для v-intersection с предзагрузкой следующих секций
const createIntersectHandler = (sectionCode: string, sectionIndex: number) => {
  const baseHandler = scrollNav.intersectDirectiveConnect(sectionCode);

  return {
    cfg: baseHandler.cfg,
    handler: (entry?: IntersectionObserverEntry) => {
      // Вызываем оригинальный handler
      const result = baseHandler.handler(entry);

      // Если секция стала видимой - предзагружаем следующие
      if (entry?.isIntersecting) {
        if (DEBUG_LAZY_LOAD) {
          console.log('[LazyLoad] Section intersecting:', {
            code: sectionCode,
            index: sectionIndex,
            currentVisited: sectionVisitedIndex.value
          });
        }

        // Предзагружаем следующие N секций
        loadMoreSections(PRELOAD_SECTIONS_COUNT);
      }

      return result;
    }
  };
};

const onSectionFiltered = (section: ProductSection, force?: boolean) => {
  if (!section.CODE) return;

  if (force)
    scrollNav.scrollTo(section.CODE);

  // Проверяем позицию элемента .c-section-top внутри секции
  const sectionTopSelector = `#${section.CODE} .c-section-top`;
  const topOffset = getElementTopOffset(sectionTopSelector, {
    // Учитываем высоту header как отступ
    offset: headerHeight.value,
  });

  console.log('topOffset', topOffset)
  // Скроллим только если элемент выше мертвой зоны от header
  // (т.е. скроллен вверх больше чем на SCROLL_DEAD_ZONE пикселей)
  if (topOffset !== null && topOffset < -SCROLL_DEAD_ZONE) {
    scrollNav.scrollTo(section.CODE);
    console.log('scroll')
  }
};

// IntersectionObserver для scrollContainer
// Очищает hash когда пользователь скроллит выше блока с секциями
const scrollContainerIntersectHandler: IntersectionValue = {
  cfg: {
    // Срабатывает когда верхняя граница контейнера пересекает верх viewport
    rootMargin: '0px 0px 0px 0px',
    threshold: [0] // Массив: срабатывает когда хотя бы 1px виден/скрыт
  },
  handler: (entry?: IntersectionObserverEntry) => {
    if (!entry || !isClientMounted.value) return true;

    // ЗАЩИТА: Игнорируем события во время начальной навигации
    // При первой загрузке с hash происходит автоматический скролл
    // и IntersectionObserver может сработать ложно
    if (isInitialNavigation) {
      if (DEBUG_LAZY_LOAD) {
        console.log('[LazyLoad] Ignoring intersection during initial navigation');
      }
      return true;
    }

    // Если контейнер НЕ виден (пользователь выше него)
    // И есть активный hash - сбрасываем на первый пункт
    if (!entry.isIntersecting) {
      if (DEBUG_LAZY_LOAD) {
        console.log('[LazyLoad] Scrolled above sections container, resetting menu to first item');
      }
      setActiveSectionSilent(null);
    }

    return true;
  }
};

// Логика свайпа между категориями (только для мобильных)
const sectionCodes = computed(() => {
  return sectionsTree.value
      .map((s: ProductSection) => s.CODE)
      .filter(Boolean) as string[];
});

const currentSectionIndex = computed(() => {
  const activeHash = toValue(scrollNav.activeHash);
  if (!activeHash) return -1;
  return sectionCodes.value.indexOf(activeHash);
});

// Следим за изменением активной секции для предзагрузки
// ВАЖНО: Проверяем isClientMounted для предотвращения проблем с SSR
watch(() => scrollNav.activeHash, (newHash) => {
  if (newHash && isClientMounted.value) {
    if (DEBUG_LAZY_LOAD) {
      console.log('[LazyLoad] Active hash changed:', newHash);
    }
    ensureSectionLoaded(newHash);
  }
});

// Целевая секция для индикатора свайпа
const targetSection = computed(() => {
  if (!swipeState.isActive.value || !swipeState.direction.value) {
    return null;
  }

  const currentIndex = currentSectionIndex.value;
  const targetIndex =
      swipeState.direction.value === "left"
          ? currentIndex + 1 // Свайп влево = следующая секция
          : currentIndex - 1; // Свайп вправо = предыдущая секция

  if (targetIndex < 0 || targetIndex >= sectionsTree.value.length) {
    return null;
  }

  return sectionsTree.value[targetIndex];
});

const goToNextSection = () => {
  const currentIndex = currentSectionIndex.value;
  // Если текущая секция не определена, начинаем с первой
  const nextIndex = currentIndex === -1 ? 0 : currentIndex + 1;

  if (nextIndex < sectionCodes.value.length) {
    const nextCode = sectionCodes.value[nextIndex];
    if (nextCode) {
      // Убеждаемся что секция загружена перед скроллом
      ensureSectionLoaded(nextCode);
      scrollNav.scrollTo(nextCode);
    }
  }
};

const goToPrevSection = () => {
  const currentIndex = currentSectionIndex.value;
  // Если текущая секция не определена, ничего не делаем
  if (currentIndex <= 0) return;

  const prevIndex = currentIndex - 1;
  const prevCode = sectionCodes.value[prevIndex];
  if (prevCode) {
    // Убеждаемся что секция загружена перед скроллом
    ensureSectionLoaded(prevCode);
    scrollNav.scrollTo(prevCode);
  }
};

const swipeState = useCategorySwipe({
  onSwipeComplete: (direction) => {
    if (direction === "next") {
      goToNextSection();
    } else if (direction === "prev") {
      goToPrevSection();
    }
  },
  threshold: 25,
  targetElement: scrollContainer,
  disabled: computed(() => !$q.platform.is.mobile),
  // Кастомная функция проверки порога - когда блок достигает середины экрана
  isThresholdReached: (progress, progressNormalized, direction) => {
    if (!direction) return false;

    const screenWidth = window.innerWidth;
    const screenCenter = screenWidth / 2;
    const blockHalfWidth = 75; // половина ширины блока (150px / 2)
    const initialPadding = 16;

    // Начальная позиция центра блока от края экрана
    const initialBlockCenter = initialPadding + blockHalfWidth;

    // Максимальное смещение к центру (35% от ширины экрана)
    const maxOffset = screenWidth * 0.35;

    // Текущее смещение блока
    const currentOffset = maxOffset * (progressNormalized / 100);

    // Определяем позицию индикатора (инвертирована относительно направления свайпа)
    const indicatorPosition = direction === "left" ? "right" : "left";

    // Текущая позиция центра блока от ЛЕВОГО края экрана
    let currentBlockCenter;
    if (indicatorPosition === "left") {
      // Блок слева, движется вправо (к центру)
      currentBlockCenter = initialBlockCenter + currentOffset;
    } else {
      // Блок справа, движется влево (к центру)
      // Начальная позиция справа = screenWidth - initialBlockCenter
      currentBlockCenter = screenWidth - initialBlockCenter - currentOffset;
    }

    // Порог срабатывает когда центр блока пересекает середину экрана
    // Используем одинаковый буфер для обоих направлений
    const thresholdBuffer = 20;

    if (indicatorPosition === "left") {
      // Блок движется слева направо - срабатывает когда достигает центра
      return currentBlockCenter >= (screenCenter - thresholdBuffer);
    } else {
      // Блок движется справа налево - срабатывает когда достигает центра
      return currentBlockCenter <= (screenCenter + thresholdBuffer);
    }
  },
});


const {pageMeta, page} = usePage("/", {
  title: "Главная",
});

useMeta(() => pageMeta.value);

// Инициализация при монтировании на клиенте
onMounted(() => {
  // Устанавливаем флаг что компонент смонтирован на клиенте
  // ВАЖНО: Делаем это первым для предотвращения race conditions
  isClientMounted.value = true;

  if (DEBUG_LAZY_LOAD) {
    console.log('[LazyLoad] Component mounted on client, initial sections:', sectionVisitedIndex.value);
  }

  // ЗАЩИТА: Устанавливаем слушатель для сброса флага начальной навигации
  // Флаг isInitialNavigation = true при инициализации (обычная переменная, не ref)
  // Сбрасываем его после первого scrollend события (любого скролла)
  // Это защищает от ложных срабатываний IntersectionObserver при начальной загрузке
  if (typeof window !== 'undefined') {
    // Слушаем первый scrollend для сброса флага
    const onFirstScrollEnd = () => {
      isInitialNavigation = false;
      if (DEBUG_LAZY_LOAD) {
        console.log('[LazyLoad] Initial navigation completed (scrollend)');
      }
    };

    window.addEventListener('scrollend', onFirstScrollEnd, {once: true});

    // Fallback таймер на случай если пользователь вообще не скроллит
    // Сбрасываем флаг через 2 секунды в любом случае
    initialNavigationTimeout = setTimeout(() => {
      if (isInitialNavigation) {
        isInitialNavigation = false;
        window.removeEventListener('scrollend', onFirstScrollEnd);
        if (DEBUG_LAZY_LOAD) {
          console.log('[LazyLoad] Initial navigation completed (timeout fallback)');
        }
      }
    }, 3000); // 2 секунды достаточно для любого автоматического скролла
  }

  bus.on('viewMode:changing', handleViewModeChanging);

  // Слушаем событие навигации к секции для предзагрузки
  bus.on('section:navigate', (sectionCode: string) => {
    if (DEBUG_LAZY_LOAD) {
      console.log('[LazyLoad] Navigation to section requested:', sectionCode);
    }
    ensureSectionLoaded(sectionCode);
  });

  // ПРИМЕЧАНИЕ: Проверка hash уже выполнена в getInitialSectionCount()
  // при инициализации sectionVisitedIndex, поэтому здесь не нужна
});

onUnmounted(() => {
  // Сбрасываем флаг при размонтировании
  isClientMounted.value = false;

  // Очищаем таймер если он еще активен
  if (initialNavigationTimeout) {
    clearTimeout(initialNavigationTimeout);
    initialNavigationTimeout = null;
  }

  bus.off('viewMode:changing', handleViewModeChanging);
  bus.off('section:navigate');
});

const handleViewModeChanging = (newMode: 'list' | 'grid') => {
  // Показываем индикатор
  catalogStore.isChangingViewMode = true;

  // Сбрасываем индикатор после завершения ререндера (250ms)
  setTimeout(() => {
    catalogStore.isChangingViewMode = false;
  }, 250);
};

// Stories данные
const stories = ref<Story[]>([
  {
    id: 1,
    name: 'Акции',
    avatar: 'https://picsum.photos/200/200?random=1',
    viewed: false,
    items: [
      {
        id: 1,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=1',
        duration: 5000,
      },
      {
        id: 2,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=2',
        duration: 5000,
      },
      {
        id: 3,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=3',
        duration: 5000,
      },
    ],
  },
  {
    id: 2,
    name: 'Новинки',
    avatar: 'https://picsum.photos/200/200?random=2',
    viewed: false,
    items: [
      {
        id: 4,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=4',
        duration: 5000,
      },
      {
        id: 5,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=5',
        duration: 5000,
      },
    ],
  },
  {
    id: 3,
    name: 'Рецепты',
    avatar: 'https://picsum.photos/200/200?random=3',
    viewed: false,
    items: [
      {
        id: 6,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=6',
        duration: 5000,
      },
      {
        id: 7,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=7',
        duration: 5000,
      },
    ],
  },
  {
    id: 4,
    name: 'Отзывы',
    avatar: 'https://picsum.photos/200/200?random=4',
    viewed: false,
    items: [
      {
        id: 8,
        type: 'image',
        url: 'https://picsum.photos/1080/1920?random=8',
        duration: 5000,
      },
    ],
  },
]);

const handleStoryViewed = (storyId: string | number, itemId: string | number) => {
  console.log('Story viewed:', {storyId, itemId});

  // Отметить историю как просмотренную когда просмотрен последний элемент
  const story = stories.value.find(s => s.id === storyId);
  if (story) {
    const itemIndex = story.items.findIndex(i => i.id === itemId);
    if (itemIndex === story.items.length - 1) {
      story.viewed = true;
    }
  }
};

const onCollectionFiltered = (collectionCode: string | null) => {
  console.log('Выбрана коллекция:', collectionCode);
};
</script>

<style lang="scss" scoped>
.global-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(2px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.global-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.global-loading-text {
  font-size: 16px;
  font-weight: 500;
  color: var(--q-primary);
}

.lazy-load-indicator {
  font-size: 14px;
  opacity: 0.7;
  user-select: none;
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
